import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { signInWithRedirect, signInWithPopup } from 'firebase/auth';
import { useSelector } from 'react-redux';
import SignUpForm from '../components/SignUpForm';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import { RootState, useLazyGetUserByFirebaseIdQuery, useUseScannerKeyMutation } from '../store';
import config from '../config/config';
import { googleAuth, auth } from '../config/firebase-config';
import OnBoardingCard from '../components/OnBoardingCard';
import OnBoardingScreens from './OnBoardingScreens';
import CustomModal from '../components/atoms/CustomModal';
import useQuery from '../hooks/useQuery';
import WhatsappFloater from '../components/WhatsappFloater';
import LoadingLottie from '../components/atoms/LoadingLottie';
import AvanttiIcon from '../components/icons/AvanttiIcon';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';

const SCANNER_KEY_STORAGE_KEY = 'scannerKey';

const SignUpPage: React.FC = () => {
  const navigate = useNavigateWithQuery();
  const [step, setStep] = useState<number>(0);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [getUserByFirebaseId] = useLazyGetUserByFirebaseIdQuery();
  const [firebaseAuthLoading, setFirebaseAuthLoading] = useState(false);
  const [isOnBoardingOpen, setIsOnBoardingOpen] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalBody, setModalBody] = useState<JSX.Element | null>(null);
  const [scannerKey, setScannerKey] = useState<string | null>(null);
  const [useScannerKey] = useUseScannerKeyMutation();
  const scannerKeyProcessedRef = useRef(false);
  const query = useQuery();
  const location = useLocation();
  const onboardingProducer = query.get('onboarding-producer') === 'true' || location.pathname.includes('/empezar') || false;
  const { token, user: stateUser } = useSelector((state: RootState) => state.auth);

  const validateEmail = (mail: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(mail);
  };

  // Store scannerKey from URL if present or recover from sessionStorage (for Google redirects)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const key = searchParams.get('scannerKey');

    if (key) {
      setScannerKey(key);
      sessionStorage.setItem(SCANNER_KEY_STORAGE_KEY, key);
      return;
    }

    const storedKey = sessionStorage.getItem(SCANNER_KEY_STORAGE_KEY);
    if (storedKey) {
      setScannerKey(storedKey);
    }
  }, [location.search]);

  useEffect(() => {
    if (stateUser?.id && token && scannerKey && !scannerKeyProcessedRef.current) {
      scannerKeyProcessedRef.current = true;
      useScannerKey({ key: scannerKey, user_id: stateUser.id });
    }
  }, [scannerKey, stateUser, token, useScannerKey]);

  useEffect(() => {
    const images = [
      '/favicon.ico',
      '/music-note-01.svg',
      '/lucide-lab_burger.svg',
      '/tabler_masks-theater.svg',
      '/onBoarding-1.svg',
      '/onBoarding-2.svg',
      '/onBoarding-3.svg',
      '/onBoarding-4.svg',
    ];

    const preloadImages = async () => {
      const promises = images.map((src) => new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
      }));
      await Promise.all(promises);
      setImagesLoaded(true);
    };

    if (onboardingProducer) {
      preloadImages();
    } else {
      setImagesLoaded(true);
    }
  }, [onboardingProducer]);

  useEffect(() => {
    if (token) {
      navigate('/admin');
    }
    if (query.get('onboarding-producer') === 'true' && token) {
      navigate('/admin/onboarding/create-business?onboarding-producer=true');
    }
  }, [token]);

  const handleSuccess = () => {
    setIsModalOpen(true);
    setModalBody(
      <div className='flex flex-col gap-4 max-w-[358px]'>
        <h1>¡Cuenta creada con éxito!</h1>
        <p>Ahora podés iniciar sesión en nuestra plataforma.</p>
        <Button variant="primary" onClick={() => {
          navigate(onboardingProducer ? '/login?firstTime=true' : '/login');
          setIsModalOpen(false);
        }}>
          Iniciar sesión
        </Button>
      </div>,
    );
  };

  const handleGoogle = () => {
    navigate('/admin');
  };

  const handleSignInWithGoogle = async () => {
    try {
      setFirebaseAuthLoading(true);
      if (config.env !== 'local') {
        return await signInWithRedirect(auth, googleAuth);
      }
      // local no funciona redirect
      const { user: fbUser } = await signInWithPopup(auth, googleAuth);
      const firebaseId = fbUser.uid;

      if (!firebaseId) {
        throw new Error('Failed to get Firebase ID');
      }
      if (!fbUser.email) {
        throw new Error('Google User has no email');
      }

      const { data: userByFbId } = await getUserByFirebaseId({ firebaseId });
      if (userByFbId) {
        handleGoogle();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setFirebaseAuthLoading(false);
    }
    return null;
  };

  const handleContinue = () => {
    if (validateEmail(email)) {
      setStep(1);
    } else if (!email) {
      setEmailError('Ingresá un correo electrónico.');
    } else {
      setEmailError('Ingresá un correo electrónico válido.');
    }
  };

  const handleOnBoardingClose = () => {
    setIsOnBoardingOpen(false);
    setIsModalOpen(true);
    setModalBody(
      <div className='flex flex-col gap-4 max-w-[358px]'>
        <h1>Primer paso, creemos tu cuenta.</h1>
        <p>Para comenzar creá tu cuenta, puede ser con mail y contraseña o login con Google.</p>
        <p>Por razones de seguridad te recomendamos que crees tu cuenta con Google, ya que es más práctico y seguro.</p>
        <Button variant="primary" onClick={() => setIsModalOpen(false)}>
          Continuar
        </Button>
      </div>,
    );
  };

  return (
    <div className='min-h-screen'>
      {onboardingProducer && !imagesLoaded && (
        <div className="fixed inset-0 bg-surface-base z-50 overflow-hidden h-full flex flex-col justify-center items-center">
          <LoadingLottie />
        </div>
      )}
      {onboardingProducer && imagesLoaded && <OnBoardingCard
        cards={OnBoardingScreens}
        mainButton={{ title: 'Comenzar', onClick: handleOnBoardingClose }}
        secondaryButton={{ title: 'Omitir', onClick: handleOnBoardingClose }}
        open={isOnBoardingOpen}
      />}
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => { }}
        hideCancelButton
      >
        {modalBody}
      </CustomModal>
      {onboardingProducer && <WhatsappFloater />}

      <div className="flex flex-col items-center admin px-6 py-6 md:py-20 my-10 bg-surface-base rounded-3xl bg-blur bg-opacity-heavy shadow-sm max-w-[820px] mx-auto">
        <div className="w-64 h-20">
          <AvanttiIcon className="w-full w-24 h-24 text-text-primary" />
        </div>
        <h1 className="text-2xl mt-8 mb-1">Crear una cuenta</h1>
        <p className="text-center text-md max-w-[358px] mb-4">Registrate en avantti para poder gestionar tus eventos desde nuestro Backoffice.</p>
        {step === 0 && <div className="flex flex-col items-center gap-6 w-full max-w-[358px]">
          <div className="flex justify-center align-items-center w-full">
            <Button
              loading={firebaseAuthLoading}
              disabled={firebaseAuthLoading}
              className="text-lg"
              type="button"
              onClick={() => handleSignInWithGoogle()}
              variant="secondary"
            >
              <div className="flex items-center align-items-center">
                <img src="/google-icon.svg" alt="Google" className="w-5 h-5 mr-2" />
                <span>Crear cuenta con Google</span>
              </div>
            </Button>
          </div>
          <div className="flex justify-center w-full">
            <div className="flex items-center gap-4 w-full md:min-w-[358px]">
              <hr className="flex-grow border-border-primary" />
              <span className="text-text-tertiary">o</span>
              <hr className="flex-grow border-border-primary" />
            </div>
          </div>
          <Input
            label="Correo electrónico"
            id="email"
            labelAbove={true}
            placeholder="Ingresá tu correo electrónico"
            onChange={(e) => setEmail(e.target.value)}
            errMsg={emailError}
          />
          <Button
            variant="primary"
            onClick={handleContinue}
          >
            Continuar con correo
          </Button>

        </div>}
        {step === 1 && <div className='w-full'>
          <SignUpForm onSuccess={handleSuccess} onGoogle={handleGoogle} mail={email} />
        </div>}
        <p className="text-center text-sm text-text-tertiary mt-6">¿Ya tenés una cuenta? <span onClick={() => navigate('/login')} className="text-[#6941C6] font-medium cursor-pointer">Inicia sesión</span></p>
      </div>
    </div >
  );
};

export default SignUpPage;
